/*
 Obfuscation for small JSON payloads carried in URLs, such as the contact
 details used to prefill a landing page (the `mus_data` param).

 This is NOT secrecy: the key ships to the browser, so anyone with the
 page's JavaScript can decrypt a token or make one. The goal is only to
 keep contact details from being readable at a glance (or matchable by
 scanners) in browser history, server and CDN logs, caches and analytics.
 Treat decrypted values as untrusted input.

 AES-256-GCM via the Web Crypto API, in the browser and in Node alike.
 GCM's integrity check makes a mangled or truncated URL fail cleanly
 instead of decoding to garbage.

 Token format: `v1.` + base64url(iv + ciphertext + GCM tag). The payload is
 `{ exp, data }`; the expiry is a staleness check, not a security control,
 since whoever makes a token chooses it.

 Keys: NEXT_PUBLIC_MUSTER_PREFILL_KEY (32 random bytes, base64
 encoded; see `yarn generate:muster-data-key`), inlined into the bundle at
 build time. NEXT_PUBLIC_MUSTER_PREFILL_KEY_PREVIOUS is also tried
 when decrypting, for rotation.
 */

const tokenPrefix = 'v1.';
const ivBytes = 12;
const tagBytes = 16;
const keyBytes = 32;

// Binds tokens to this purpose so they can't be confused with anything
// else encrypted under the same key. Encoded on use, so importing this
// module never needs TextEncoder.
const additionalData = 'mus_data:v1';

export const defaultTtlSeconds = 24 * 60 * 60;

interface Envelope {
  exp: number;
  data: unknown;
}

interface EncryptOptions {
  ttlSeconds?: number;
}

const keyCache = new Map<string, Promise<CryptoKey>>();
let hasWarnedMissingKey = false;
let hasWarnedNoWebCrypto = false;

// Browsers only provide crypto.subtle in secure contexts: HTTPS or
// localhost, and for an iframe, only if every ancestor page is too. Plain
// HTTP on a LAN address or local hostname, or embedding in an HTTP page,
// has none, so mus_data links can't be built or read there.
const getSubtle = (): SubtleCrypto => {
  const subtle = globalThis.crypto?.subtle;

  if (!subtle) {
    if (!hasWarnedNoWebCrypto) {
      hasWarnedNoWebCrypto = true;
      // eslint-disable-next-line no-console
      console.warn('Web Crypto (crypto.subtle) is unavailable, likely because this page is not a secure context (HTTPS or localhost); mus_data prefill links are disabled');
    }

    throw new Error('Web Crypto is not available');
  }

  return subtle;
};

const toBase64Url = (bytes: Uint8Array): string => {
  let binary = '';

  bytes.forEach(byte => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

const fromBase64 = (value: string): Uint8Array | null => {
  try {
    return Uint8Array.from(atob(value), character => character.charCodeAt(0));
  } catch {
    return null;
  }
};

const fromBase64Url = (value: string): Uint8Array | null => {
  if (!/^[A-Za-z0-9_-]+$/.test(value)) {
    return null;
  }

  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');

  return fromBase64(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='));
};

const importKey = (encodedKey: string): Promise<CryptoKey> => {
  const cached = keyCache.get(encodedKey);

  if (cached) {
    return cached;
  }

  const rawKey = fromBase64(encodedKey.trim());

  if (!rawKey || rawKey.length !== keyBytes) {
    return Promise.reject(new Error(`Encryption key must be ${keyBytes} bytes, base64 encoded`));
  }

  const key = getSubtle().importKey('raw', rawKey, 'AES-GCM', false, [
    'encrypt',
    'decrypt',
  ]);

  keyCache.set(encodedKey, key);

  return key;
};

// Both env vars must be referenced literally so Next can inline them
const getCurrentKey = (): string | undefined => {
  const encodedKey = process.env.NEXT_PUBLIC_MUSTER_PREFILL_KEY;

  if (!encodedKey && !hasWarnedMissingKey) {
    hasWarnedMissingKey = true;
    // eslint-disable-next-line no-console
    console.warn('NEXT_PUBLIC_MUSTER_PREFILL_KEY is not set; mus_data prefill links are disabled');
  }

  return encodedKey;
};

const getPreviousKey = (): string | undefined => (
  process.env.NEXT_PUBLIC_MUSTER_PREFILL_KEY_PREVIOUS
);

/*
 Encrypts a JSON-serializable value into a URL-safe token that expires
 after `ttlSeconds`. Throws if the key is missing or malformed; callers
 must then go without the data, never fall back to plaintext.
 */
export const encryptJson = async (
  value: unknown,
  { ttlSeconds = defaultTtlSeconds }: EncryptOptions = {}
): Promise<string> => {
  const encodedKey = getCurrentKey();

  if (!encodedKey) {
    throw new Error('NEXT_PUBLIC_MUSTER_PREFILL_KEY is not set');
  }

  const key = await importKey(encodedKey);
  const envelope: Envelope = {
    data: value,
    exp: Math.floor(Date.now() / 1000) + ttlSeconds,
  };
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(ivBytes));
  const ciphertext = await getSubtle().encrypt(
    {
      additionalData: new TextEncoder().encode(additionalData),
      iv,
      name: 'AES-GCM',
    },
    key,
    new TextEncoder().encode(JSON.stringify(envelope))
  );

  const token = new Uint8Array(ivBytes + ciphertext.byteLength);

  token.set(iv);
  token.set(new Uint8Array(ciphertext), ivBytes);

  return `${tokenPrefix}${toBase64Url(token)}`;
};

const decryptWithKey = async (encodedKey: string, iv: Uint8Array, ciphertext: Uint8Array) => {
  try {
    const key = await importKey(encodedKey);
    const plaintext = await getSubtle().decrypt(
      {
        additionalData: new TextEncoder().encode(additionalData),
        iv,
        name: 'AES-GCM',
      },
      key,
      ciphertext
    );

    return new TextDecoder().decode(plaintext);
  } catch {
    // Wrong key, tampered token, or a malformed key in the environment
    return null;
  }
};

/*
 Decrypts a token from encryptJson. Returns null, never throws, when the
 token is malformed, tampered with, expired, or encrypted under a key that
 isn't configured.
 */
export const decryptJson = async <T = unknown>(token: string): Promise<T | null> => {
  if (typeof token !== 'string' || !token.startsWith(tokenPrefix)) {
    return null;
  }

  const bytes = fromBase64Url(token.slice(tokenPrefix.length));

  if (!bytes || bytes.length <= ivBytes + tagBytes) {
    return null;
  }

  const iv = bytes.slice(0, ivBytes);
  const ciphertext = bytes.slice(ivBytes);
  const encodedKeys = [
    getCurrentKey(),
    getPreviousKey(),
  ].filter((encodedKey): encodedKey is string => !!encodedKey);

  // Try each key in turn until one decrypts
  const plaintext = await encodedKeys.reduce<Promise<string | null>>(
    async (decrypted, encodedKey) => (
      (await decrypted) ?? decryptWithKey(encodedKey, iv, ciphertext)
    ),
    Promise.resolve(null)
  );

  if (plaintext === null) {
    return null;
  }

  try {
    const envelope = JSON.parse(plaintext) as Envelope;

    if (typeof envelope?.exp !== 'number' || envelope.exp * 1000 <= Date.now()) {
      return null;
    }

    return envelope.data as T;
  } catch {
    return null;
  }
};
